import {
	type BulkAclEntity,
	type EditorCompositionResponse,
	type UserInstrumentDto,
	AclEntityMinInfoEntityTypeEnum,
	EntityEditorControllerApiFactory,
	IntegrationUniverseControllerApiFactory,
	MarketAssetClassesControllerApiFactory,
	ReferenceUniversesControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { type EventPayloadMap, eventBus } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { downloadContentDisposition, seconds } from "$root/utils";
import type { Abortable } from "@mdotm/utils/promise";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import debounce from "debounce";
import type { Map } from "immutable";
import { reviewTickerToInstrumentsMap } from "../instruments/repository";

const universeRepositoryBaseKeys = {
	list: "universeList",
};

const { newQuery, newAction, invalidateEntity, invalidateLists, invalidateEntities } = repositoryHelperFactory(
	"universe",
	{
		listKeys: Object.values(universeRepositoryBaseKeys),
	},
);
const universeApi = () => getApiGen(ReferenceUniversesControllerApiFactory);
const editorApi = () => getApiGen(EntityEditorControllerApiFactory);
const integrationApi = () => getApiGen(IntegrationUniverseControllerApiFactory);
const marketApi = () => getApiGen(MarketAssetClassesControllerApiFactory);

export const universeRepository = {
	invalidateLists,

	listBaseAndGroup: newQuery(universeRepositoryBaseKeys.list, () =>
		axiosExtract(universeApi().getUserUniverses()).then((list) => list.sort(builtInSortFnFor("creationTime", "desc"))),
	),

	summary: newQuery("summary", (uuid: string) => axiosExtract(universeApi().getUniverse(uuid))),

	composition: newQuery("composition", (uuid: string) => axiosExtract(universeApi().getUniverseComposition(uuid))),

	reviewEditorComposition: newQuery("reviewInstrumentComposition", (uuid: string) =>
		axiosExtract(editorApi().getEditorReviewComposition(uuid, "UNIVERSE")),
	),

	reviewEditorCompositionWithInstrumentMap: newQuery(
		"reviewEditorCompositionWithInstrumentMap",
		async (
			uuid: string,
		): Promise<{ editorResponse: EditorCompositionResponse; instrumentsMap: Map<string, UserInstrumentDto> }> => {
			const editorResponse = await universeRepository.reviewEditorComposition.fetchQuery(uuid);
			const instrumentsMap = await reviewTickerToInstrumentsMap(editorResponse.composition ?? []);
			return { editorResponse, instrumentsMap };
		},
	),

	getACL: newQuery("acl", (uuid: string) => axiosExtract(universeApi().getAclEditStarter(uuid))),

	updateACL: newAction(async (uuid: string, acl: Array<RichAccessControl>) => {
		await universeApi().setACL(uuid, acl);
		invalidateEntity(uuid);
	}),

	rename: newAction(async (uuid: string, name: string) => {
		await axiosExtract(universeApi().renameUniverse(uuid, name));

		invalidateEntity(uuid);
	}),

	duplicate: newAction(async (uuid: string, name: string) => {
		const clone = await axiosExtract(universeApi().duplicateUniverse(uuid, name));

		invalidateLists();
		return clone;
	}),

	delete: newAction(async (uuid: string) => {
		await axiosExtract(universeApi().deleteUniverse(uuid));

		invalidateLists();
	}),

	isUniverseNameAvailable: newAction((uuid: string, opts?: Abortable) => {
		return axiosExtract(universeApi().isUniverseNameAvailable(uuid, opts));
	}),

	downloadComposition: newAction(async (uuid: string) => {
		const response = await universeApi().exportUniverseComposition(uuid, {
			responseType: "blob",
		});

		return downloadContentDisposition(response);
	}),
	downloadUniverse: newAction(async (uuid: string) => {
		const response = await integrationApi().exportUniverse(uuid, {
			responseType: "blob",
		});

		return downloadContentDisposition(response);
	}),
	/** `persist=false` downloads the file now; `persist=true` builds it in the background and notifies the user. */
	downloadBulkTemplate: newAction(async (uuids: string[], persist: boolean) => {
		if (persist) {
			await integrationApi().exportUniversesBulkTemplate(uuids, true);
			return;
		}
		const response = await integrationApi().exportUniversesBulkTemplate(uuids, false, { responseType: "blob" });
		await downloadContentDisposition(response);
	}),

	marketAssetClasses: newQuery("getMarketAssetClasses", () => axiosExtract(marketApi().getMarketAssetClasses())),
	/** handles both universes and universes-with-groups */
	bulkShare: newAction(async (bulkAclEntities: BulkAclEntity[]) => {
		await universeApi().setBulkACL(bulkAclEntities);
		invalidateEntities(bulkAclEntities.map((entity) => entity.entityUuid!));
	}),

	universeBulkAclPreview: newQuery("universeBulkAclPreview", (acl: Array<BulkAclEntity>) =>
		axiosExtract(universeApi().getBulkACLCheck(acl)),
	),
} as const;

eventBus.addListener(
	"investment-update",
	debounce((e: EventPayloadMap["investment-update"]) => {
		if (e.type === "UNIVERSE_UPDATE") {
			invalidateEntity(e.uuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entity",
	debounce((e: EventPayloadMap["shared-entity"]) => {
		if (e.sharedEntityType === AclEntityMinInfoEntityTypeEnum.Universe) {
			invalidateEntity(e.sharedEntityUuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entities",
	debounce((e: EventPayloadMap["shared-entities"]) => {
		const referenceEntities = e.investments?.filter((x) => x.sharedEntityType === "UNIVERSE");
		if (referenceEntities?.length && referenceEntities?.length > 0) {
			invalidateEntities(referenceEntities.flatMap((x) => (x.sharedEntityUuid ? [x.sharedEntityUuid] : [])));
		}
	}, seconds(2)),
);
